import createClient from 'openapi-fetch';
import type { paths } from './schema';

/** Erreur renvoyée par l'API au format stable `{status, code, message, fields?}` (jamais de donnée chiffrée dedans). */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

/** Le réseau ne répond pas (hors ligne, serveur injoignable) : distinct d'une réponse d'erreur du serveur. */
export class NetworkFailure extends Error {
  constructor() {
    super('Le serveur est injoignable.');
    this.name = 'NetworkFailure';
  }
}

/** Jetons de session, en mémoire uniquement (un rechargement de page redemande le mot de passe maître). */
export class TokenStore {
  access: string | null = null;
  refresh: string | null = null;

  set(access: string, refresh: string): void {
    this.access = access;
    this.refresh = refresh;
  }

  clear(): void {
    this.access = null;
    this.refresh = null;
  }
}

export interface ApiOptions {
  tokens: TokenStore;
  /** Appelé quand le jeton de renouvellement est refusé : la session est perdue. */
  onSessionLost?: () => void;
  baseFetch?: typeof fetch;
  /** Préfixe d'origine des requêtes ; vide dans le navigateur (même origine que l'application). */
  baseUrl?: string;
}

const authPath = (url: string): boolean => new URL(url, 'http://local').pathname.startsWith('/api/v1/auth/');

export function createApi({ tokens, onSessionLost, baseFetch, baseUrl = '' }: ApiOptions) {
  const doFetch = (request: Request): Promise<Response> => (baseFetch ?? fetch)(request);
  let refreshing: Promise<boolean> | null = null;

  const withBearer = (request: Request): Request => {
    const headers = new Headers(request.headers);
    if (tokens.access) headers.set('Authorization', `Bearer ${tokens.access}`);
    return new Request(request, { headers });
  };

  const refresh = (): Promise<boolean> => {
    refreshing ??= (async () => {
      try {
        const response = await doFetch(
          new Request(`${baseUrl}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: tokens.refresh }),
          }),
        );
        if (!response.ok) return false;
        const body = (await response.json()) as { accessToken: string; refreshToken: string };
        tokens.set(body.accessToken, body.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
    return refreshing;
  };

  const authFetch = async (input: Request): Promise<Response> => {
    const isAuth = authPath(input.url);
    const retry = input.clone();
    let response: Response;
    try {
      response = await doFetch(isAuth ? input : withBearer(input));
    } catch {
      throw new NetworkFailure();
    }
    if (response.status === 401 && !isAuth && tokens.refresh) {
      if (await refresh()) {
        try {
          return await doFetch(withBearer(retry));
        } catch {
          throw new NetworkFailure();
        }
      }
      tokens.clear();
      onSessionLost?.();
    }
    return response;
  };

  return createClient<paths>({ baseUrl, fetch: authFetch });
}

export type Api = ReturnType<typeof createApi>;

/** Renvoie les données d'une réponse ou lève `ApiFailure` avec le code stable du serveur. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    const e = (result.error ?? {}) as { code?: string; message?: string };
    const retry = Number(result.response.headers.get('Retry-After'));
    throw new ApiFailure(result.response.status, e.code ?? 'ERROR', e.message ?? 'La requête a échoué.', Number.isFinite(retry) && retry > 0 ? retry : undefined);
  }
  return result.data as T;
}
